import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import * as React from 'react';
import { ButtonVariant, buttonVariants } from '@components/molecules';
import type { ClassNameProp } from '@utils';
import { cn } from '@utils';

// Main AlertDialog component from Radix UI
const AlertDialog: React.FC<
	AlertDialogPrimitive.AlertDialogProps & {
		className?: string;
	}
> = AlertDialogPrimitive.Root;

// Component to trigger the alert dialog's visibility
const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

// Portal component for rendering the alert dialog into a different part of the DOM
const AlertDialogPortal = AlertDialogPrimitive.Portal;

type AlertDialogOverlayPrimitiveProps = AlertDialogPrimitive.AlertDialogOverlayProps & {
	className?: string;
};

/**
 * Overlay component for the alert dialog, providing a backdrop.
 */
const AlertDialogOverlayPrimitive = React.forwardRef<HTMLDivElement, AlertDialogOverlayPrimitiveProps>(
	({ ...props }, ref) => <AlertDialogPrimitive.Overlay {...props} ref={ref} />
);
AlertDialogOverlayPrimitive.displayName = 'AlertDialogOverlayPrimitive';

/**
 * Styled overlay component for the alert dialog.
 */
const AlertDialogOverlay = React.forwardRef<
	React.ElementRef<typeof AlertDialogOverlayPrimitive>,
	React.ComponentPropsWithoutRef<typeof AlertDialogOverlayPrimitive>
>(({ className, ...props }, ref) => (
	<AlertDialogOverlayPrimitive
		className={cn(
			'fixed inset-0 z-1000 bg-bgOverlay data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
			className
		)}
		{...props}
		ref={ref}
	/>
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

type AlertDialogContentPrimitiveProps = React.PropsWithChildren<
	AlertDialogPrimitive.AlertDialogContentProps & ClassNameProp
>;

/**
 * Primitive content component for the alert dialog.
 */
const AlertDialogContentPrimitive = React.forwardRef<HTMLDivElement, AlertDialogContentPrimitiveProps>(
	({ ...props }, ref) => <AlertDialogPrimitive.Content {...props} ref={ref} />
);
AlertDialogContentPrimitive.displayName = 'AlertDialogContentPrimitive';

/**
 * Styled content component for the alert dialog.
 */
const AlertDialogContent = React.forwardRef<
	React.ElementRef<typeof AlertDialogContentPrimitive>,
	React.ComponentPropsWithoutRef<typeof AlertDialogContentPrimitive>
>(({ className, ...props }, ref) => (
	<AlertDialogPortal>
		<AlertDialogOverlay />
		<AlertDialogContentPrimitive
			ref={ref}
			aria-modal="true"
			className={cn(
				'fixed top-[50%] left-[50%] z-1000 flex w-full max-w-lg translate-x-[-50%] translate-y-[-50%] flex-col gap-4 bg-white px-10 pt-8 pb-4 duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
				className
			)}
			{...props}
		/>
	</AlertDialogPortal>
));
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

/**
 * Header component for the alert dialog, typically containing the title or other header content.
 */
const AlertDialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div className={cn('flex flex-col space-y-2 text-center text-primary', className)} {...props} />
);
AlertDialogHeader.displayName = 'AlertDialogHeader';

/**
 * Footer component for the alert dialog, typically containing actions related to the dialog's content.
 */
const AlertDialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div className={cn('flex flex-row space-x-4 pt-4', className)} {...props} />
);
AlertDialogFooter.displayName = 'AlertDialogFooter';

type AlertDialogTitlePrimitiveProps = React.PropsWithChildren<
	AlertDialogPrimitive.AlertDialogTitleProps & ClassNameProp
>;

/**
 * Primitive title component for the alert dialog.
 */
const AlertDialogTitlePrimitive = React.forwardRef<HTMLHeadingElement, AlertDialogTitlePrimitiveProps>(
	({ ...props }, ref) => <AlertDialogPrimitive.Title {...props} ref={ref} />
);
AlertDialogTitlePrimitive.displayName = 'AlertDialogTitlePrimitive';

/**
 * Styled title component for the alert dialog.
 */
const AlertDialogTitle = React.forwardRef<
	React.ElementRef<typeof AlertDialogTitlePrimitive>,
	React.ComponentPropsWithoutRef<typeof AlertDialogTitlePrimitive>
>(({ className, ...props }, ref) => (
	<AlertDialogTitlePrimitive ref={ref} className={cn('text-lg font-semibold', className)} {...props} />
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

type AlertDialogDescriptionPrimitiveProps = React.PropsWithChildren<
	AlertDialogPrimitive.AlertDialogDescriptionProps & ClassNameProp
>;

/**
 * Primitive description component for the alert dialog.
 */
const AlertDialogDescriptionPrimitive = React.forwardRef<HTMLParagraphElement, AlertDialogDescriptionPrimitiveProps>(
	({ ...props }, ref) => <AlertDialogPrimitive.Description {...props} ref={ref} />
);
AlertDialogDescriptionPrimitive.displayName = 'AlertDialogDescriptionPrimitive';

/**
 * Styled description component for the alert dialog.
 */
const AlertDialogDescription = React.forwardRef<
	React.ElementRef<typeof AlertDialogDescriptionPrimitive>,
	React.ComponentPropsWithoutRef<typeof AlertDialogDescriptionPrimitive>
>(({ className, ...props }, ref) => (
	<AlertDialogDescriptionPrimitive ref={ref} className={cn('text-muted-foreground text-sm', className)} {...props} />
));
AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName;

type AlertDialogActionPrimitiveProps = React.PropsWithChildren<
	AlertDialogPrimitive.AlertDialogActionProps & ClassNameProp
>;

/**
 * Primitive action button component for the alert dialog.
 */
const AlertDialogActionPrimitive = React.forwardRef<HTMLButtonElement, AlertDialogActionPrimitiveProps>(
	({ ...props }, ref) => <AlertDialogPrimitive.Action {...props} ref={ref} />
);
AlertDialogActionPrimitive.displayName = 'AlertDialogActionPrimitive';

/**
 * Styled action button component for the alert dialog.
 */
const AlertDialogAction = React.forwardRef<
	React.ElementRef<typeof AlertDialogActionPrimitive>,
	React.ComponentPropsWithoutRef<typeof AlertDialogActionPrimitive>
>(({ className, ...props }, ref) => (
	<AlertDialogActionPrimitive ref={ref} className={cn(buttonVariants(), className)} {...props} />
));
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

type AlertDialogCancelPrimitiveProps = React.PropsWithChildren<
	AlertDialogPrimitive.AlertDialogCancelProps & ClassNameProp
>;

/**
 * Primitive cancel button component for the alert dialog.
 */
const AlertDialogCancelPrimitive = React.forwardRef<HTMLButtonElement, AlertDialogCancelPrimitiveProps>(
	({ ...props }, ref) => <AlertDialogPrimitive.Cancel {...props} ref={ref} />
);
AlertDialogCancelPrimitive.displayName = 'AlertDialogCancelPrimitive';

/**
 * Styled cancel button component for the alert dialog.
 */
const AlertDialogCancel = React.forwardRef<
	React.ElementRef<typeof AlertDialogCancelPrimitive>,
	React.ComponentPropsWithoutRef<typeof AlertDialogCancelPrimitive>
>(({ className, ...props }, ref) => (
	<AlertDialogCancelPrimitive
		ref={ref}
		className={cn(buttonVariants({ variant: ButtonVariant.SECONDARY }), 'mt-2 sm:mt-0', className)}
		{...props}
	/>
));
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

// Exporting all components for use in other parts of the application
export {
	AlertDialog,
	AlertDialogPortal,
	AlertDialogOverlay,
	AlertDialogTrigger,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogFooter,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogAction,
	AlertDialogCancel,
};
